import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowLeft, CaretDown, CaretUp, CheckCircle, PencilSimple, Plus } from '@phosphor-icons/react';
import { TASK_TABS, TASK_TYPES, can, type TaskTab, type TaskType } from '@mybusinessmate/schema';
import { IdentityHeader, IdentityTile } from '../../../../../../components/shell/identity-header';
import { TaskList } from '../../../../../../components/tasks/task-list';
import { Icon } from '../../../../../../components/Icon';
import { cx } from '../../../../../../lib/cx';
import { Chip } from '../../../../../../components/ui/chip';
import { Card } from '../../../../../../components/ui/card';
import { EmptyState } from '../../../../../../components/ui/empty-state';
import { ProgressBar } from '../../../../../../components/ui/progress-bar';
import { SegmentedControl } from '../../../../../../components/ui/segmented-control';
import { Toggle } from '../../../../../../components/ui/toggle';
import { PageSkeleton } from '../../../../../../components/ui/skeleton';
import { LoadFailure, NoResults } from '../../../../../../components/ui/state-views';
import { textLinkClass } from '../../../../../../components/ui/text-link';
import { StatusTag } from '../../../../../../components/ui/status-tag';
import { useToast } from '../../../../../../components/ui/toast';
import {
  FilterSelectPill,
  MoreButton,
  pillClass,
  ToolbarSearch,
} from '../../../../../../components/ui/toolbar-controls';
import { businessQuery } from '../../../../../../lib/directory-api';
import { DOMAIN_META, formatDayYear, formatDay, projectStatusDisplay } from '../../../../../../lib/project-display';
import { projectQuery } from '../../../../../../lib/projects-api';
import { useSession } from '../../../../../../lib/session';
import { isStaffRoleName } from '../../../../../../lib/staff-nav';
import { TASK_TYPE_META } from '../../../../../../lib/task-display';
import { taskListQuery, workersQuery } from '../../../../../../lib/tasks-api';
import { useDebouncedValue } from '../../../../../../lib/use-debounced-value';
import { toasts } from '../../../../../../lib/toast-presets';

const TAB_LABEL: Record<TaskTab, string> = { open: 'Open', done: 'Done', all: 'All' };

export const Route = createFileRoute('/subdomain/admin/_staff/projects/$projectId/')({
  // Filters live in the URL so a reload or a shared link shows the same list. Optional, so links to the project need
  // not name them: no tab means Open.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: TaskTab; q?: string; type?: TaskType; assignee?: string } => ({
    ...((TASK_TABS as readonly unknown[]).includes(search.tab) ? { tab: search.tab as TaskTab } : {}),
    ...(typeof search.q === 'string' && search.q ? { q: search.q } : {}),
    ...((TASK_TYPES as readonly unknown[]).includes(search.type) ? { type: search.type as TaskType } : {}),
    ...(typeof search.assignee === 'string' && search.assignee ? { assignee: search.assignee } : {}),
  }),
  // The page is 1200 wide in the design (same canvas as the tables).
  staticData: { wide: true },
  component: ProjectPage,
});

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-(--mbm-space-4)">
      <dt className="text-body-small text-text-secondary">{label}</dt>
      <dd className="text-label-medium text-text-primary">{children}</dd>
    </div>
  );
}

/** "✓ 2 completed tasks ⌄": finished work stays folded away under the open list until asked for. */
function CompletedTasks({
  projectId,
  count,
  q,
  type,
  assigneeId,
}: {
  projectId: string;
  count: number;
  q?: string;
  type?: TaskType;
  assigneeId?: string;
}) {
  const [open, setOpen] = useState(false);
  const done = useInfiniteQuery({ ...taskListQuery({ projectId, tab: 'done', q, type, assigneeId }), enabled: open });
  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-(--mbm-space-2) border-t border-border-line px-(--mbm-space-6) py-(--mbm-space-4) text-left text-label-medium text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        <span className="text-feedback-success">
          <Icon icon={CheckCircle} size="md" />
        </span>
        {count} completed {count === 1 ? 'task' : 'tasks'}
        <Icon icon={open ? CaretUp : CaretDown} size="sm" />
      </button>
      {open ? <TaskList query={done} audience="staff" emptyState={null} /> : null}
    </>
  );
}

/** The project's tasks: search, Open / Done / All with counts, the Type filter, and the sections. */
function TasksPanel({ projectId, canCreate }: { projectId: string; canCreate: boolean }) {
  const { tab = 'open', q, type, assignee } = Route.useSearch();
  const { data: session } = useSession();
  const navigate = useNavigate({ from: Route.fullPath });
  const [text, setText] = useState(q ?? '');
  const search = useDebouncedValue(text.trim(), 250) || undefined;
  if (search !== q) void navigate({ search: (prev) => ({ ...prev, q: search }), replace: true });

  const query = useInfiniteQuery(taskListQuery({ projectId, tab, q: search, type, assigneeId: assignee }));
  const counts = query.data?.pages[0]?.counts;
  const filtered = !!search || !!type || !!assignee || tab !== 'open';
  // Specialists cannot pick people (the API refuses), and they only ever see their own tasks anyway.
  const role = session?.user.role;
  const workers = useQuery({
    ...workersQuery(''),
    enabled: !!role && role !== 'specialist' && role !== 'client',
    retry: false,
  });
  const assignees = [
    { value: 'none', label: 'Unassigned' },
    ...(workers.data ?? []).map((w) => ({ value: w.id, label: w.name })),
  ];

  return (
    <div className="flex flex-col gap-(--mbm-space-4)">
      <div className="flex flex-wrap items-center gap-(--mbm-space-2-5)">
        <ToolbarSearch
          filled
          size="lg"
          aria-label="Search tasks"
          placeholder="Search tasks"
          value={text}
          onChange={setText}
          className="w-full sm:w-(--mbm-layout-toolbar-search-narrow-width)"
        />
        <SegmentedControl
          shape="pill"
          size="lg"
          aria-label="Task status"
          options={TASK_TABS.map((value) => ({ value, label: TAB_LABEL[value], count: counts?.[value] }))}
          value={tab}
          onChange={(next) => void navigate({ search: (prev) => ({ ...prev, tab: next }) })}
        />
        <span className="ml-auto flex flex-wrap items-center gap-(--mbm-space-2-5)">
          <FilterSelectPill
            label="Type"
            size="lg"
            showAll
            value={type ?? ''}
            onChange={(value) =>
              void navigate({ search: (prev) => ({ ...prev, type: (value || undefined) as TaskType | undefined }) })
            }
            options={TASK_TYPES.map((t) => ({ value: t, label: TASK_TYPE_META[t].label }))}
          />
          {role !== 'specialist' ? (
            <FilterSelectPill
              label="Assignee"
              size="lg"
              showAll
              allText="Anyone"
              value={assignee ?? ''}
              onChange={(value) => void navigate({ search: (prev) => ({ ...prev, assignee: value || undefined }) })}
              options={assignees}
            />
          ) : null}
        </span>
      </div>
      <Card tone="panel" padding="none" className="overflow-hidden pb-(--mbm-space-2)">
        <TaskList
          query={query}
          audience="staff"
          emptyState={
            filtered ? (
              <NoResults
                query={search}
                description="Try another tab, filter or search."
                onClear={() => {
                  setText('');
                  void navigate({ search: (prev) => ({ ...prev, tab: undefined, assignee: undefined, q: undefined }) });
                }}
              />
            ) : (
              <EmptyState
                title="No tasks yet"
                description="Create the first one to get the team started."
                action={
                  canCreate && !filtered ? (
                    <Link
                      to="/subdomain/admin/projects/$projectId/tasks/new"
                      params={{ projectId }}
                      search={{ type: 'post' }}
                      className={pillClass('dark', 'lg')}
                    >
                      <Icon icon={Plus} size="sm" />
                      New task
                    </Link>
                  ) : undefined
                }
              />
            )
          }
        />
        {tab === 'open' && counts && counts.done > 0 ? (
          <CompletedTasks projectId={projectId} count={counts.done} q={search} type={type} assigneeId={assignee} />
        ) : null}
      </Card>
    </div>
  );
}

/**
 * Staff project page (Figma A05.2): the tasks, the stored progress rollup, and what the project is.
 * TODO(api): the design's project milestones (the checkpoints under the percentage), "Created automatically · Plus plan",
 * deliverables count and the "Visible to client" switch need a project-level milestones table, billing and a
 * `projects.client_visible` column; they are left out of the layout, not faked.
 */
function ProjectPage() {
  const { projectId } = Route.useParams();
  const { data: session } = useSession();
  const role = session?.user.role;
  const toast = useToast();
  const { data: project, isLoading, error, refetch } = useQuery(projectQuery(projectId));
  const business = useQuery({
    ...businessQuery(project?.businessId ?? ''),
    enabled: !!project?.businessId,
    retry: false,
  });
  const soon = () => toast(toasts.comingSoon('Editing a project arrives with its own step.'));
  const soonMilestones = () => toast(toasts.comingSoon('Project milestones arrive with their own step.'));

  if (isLoading) return <PageSkeleton />;

  if (!project) {
    return (
      <LoadFailure
        error={error}
        noun="project"
        forbiddenHint="Ask an admin to assign you to it."
        onRetry={() => void refetch()}
      />
    );
  }

  // Staff call a running project "Active" (Figma A05.2); the client's own wording is "In progress".
  const status =
    project.status === 'In Progress'
      ? { label: 'Active', tone: 'green' as const }
      : projectStatusDisplay(project.status);
  const businessId = project.businessId;
  // Only admins and the business's account manager create tasks; the API enforces it, this hides the button.
  const canCreate =
    isStaffRoleName(role) && can({ role }, 'task.create', { managesBusiness: role === 'account_manager' });
  const primaryManager = business.data?.accountManagers.find((m) => m.isPrimary) ?? business.data?.accountManagers[0];

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        className="flex flex-wrap items-center gap-(--mbm-space-2) text-label-medium text-text-secondary"
      >
        <Link
          to="/subdomain/admin/businesses"
          search={{ tab: undefined, q: undefined, am: undefined }}
          className="inline-flex items-center gap-(--mbm-space-2) hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={ArrowLeft} size="sm" />
          Businesses
        </Link>
        {businessId ? (
          <>
            <span aria-hidden>/</span>
            <Link
              to="/subdomain/admin/businesses/$businessId"
              params={{ businessId }}
              className="hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              {project.businessName ?? 'Business'}
            </Link>
            <span aria-hidden>/</span>
            <Link
              to="/subdomain/admin/businesses/$businessId/projects"
              params={{ businessId }}
              search={{ bucket: 'active', domain: undefined, q: undefined }}
              className="hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              Projects
            </Link>
          </>
        ) : null}
        <span aria-hidden>/</span>
        <span className="text-text-primary">{project.name}</span>
      </nav>

      <IdentityHeader
        tile={
          <IdentityTile>
            <Icon icon={DOMAIN_META[project.domain].icon} size="xl" />
          </IdentityTile>
        }
        title={project.name}
        meta={
          <>
            <StatusTag
              size="md"
              label={status.label}
              tone={status.tone}
              variant={status.tone === 'grey' ? 'outline' : 'soft'}
            />
            {/* TODO(api): every project is visible to its client until there is a column to say otherwise. */}
            <Chip size="md">Visible to client</Chip>
            <span>
              {DOMAIN_META[project.domain].label} project · started {formatDay(project.startDate)} ·{' '}
              {project.taskCount === 1 ? '1 task' : `${project.taskCount} tasks`}
            </span>
          </>
        }
        actions={
          <>
            <button type="button" onClick={soon} className={pillClass('light', 'lg')}>
              <Icon icon={PencilSimple} size="sm" />
              Edit project
            </button>
            {canCreate ? (
              <Link
                to="/subdomain/admin/projects/$projectId/tasks/new"
                params={{ projectId: project.id }}
                search={{ type: 'post' }}
                className={pillClass('dark', 'lg')}
              >
                <Icon icon={Plus} size="sm" />
                New task
              </Link>
            ) : null}
            <MoreButton onClick={soon} />
          </>
        }
      />

      <div className="grid items-start gap-(--mbm-space-6) lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <TasksPanel projectId={project.id} canCreate={canCreate} />

        <div className="flex flex-col gap-(--mbm-space-4)">
          <Card tone="panel" className="flex flex-col gap-(--mbm-space-4)">
            <div className="flex items-start justify-between gap-(--mbm-space-3)">
              <div className="flex flex-col gap-(--mbm-space-0-5)">
                <h2 className="text-heading-h3 text-text-primary">Project progress</h2>
                {/* TODO(kb): Figma says "weighted by effort"; the KB stores a plain average of task progress. */}
                <p className="text-body-small text-text-secondary">Average of task progress</p>
              </div>
              <span className="text-heading-h1 text-text-strong">{project.progress}%</span>
            </div>
            <ProgressBar value={project.progress} tone="dark" label="Project progress" />
            {/* TODO(api): the design lists the project's own milestones here (dated checkpoints with a target %);
                there is no table for them yet, so there is nothing to list and the button is inert. */}
            <button
              type="button"
              onClick={soonMilestones}
              className={cx(textLinkClass, 'inline-flex items-center gap-(--mbm-space-2) self-start')}
            >
              <Icon icon={Plus} size="sm" />
              Add project milestone
            </button>
          </Card>

          <Card tone="panel" className="flex flex-col gap-(--mbm-space-3)">
            <h2 className="text-heading-h3 text-text-primary">About this project</h2>
            <dl className="flex flex-col gap-(--mbm-space-3)">
              <Fact label="Domain">{DOMAIN_META[project.domain].label}</Fact>
              <Fact label="Started">{formatDayYear(project.startDate)}</Fact>
              <Fact label="Target">{project.targetEndDate ? formatDayYear(project.targetEndDate) : 'Not set'}</Fact>
              {/* TODO(api): who or what created the project (a plan, an add-on order, a person) is not recorded. */}
              <Fact label="Created">—</Fact>
              {primaryManager ? <Fact label="Account manager">{primaryManager.name}</Fact> : null}
              {/* TODO(api): deliverables (the vault) arrive with their own step. */}
              <Fact label="Deliverables">—</Fact>
            </dl>
            {/* TODO(api): projects have no "visible to client" column yet; every project shows to its client. */}
            <div className="flex items-center justify-between gap-(--mbm-space-4) border-t border-border-line pt-(--mbm-space-3)">
              <div className="flex min-w-0 flex-col">
                <span className="text-label-medium text-text-primary">Visible to client</span>
                <span className="text-body-xsmall text-text-tertiary">
                  Hiding it also hides all its tasks from the client
                </span>
              </div>
              <Toggle aria-label="Visible to client" checked disabled onCheckedChange={() => {}} />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
